import { useInfiniteQuery } from 'react-query';
import { storeMeta } from '../../../../actions/metaActions';
import { failedSearch, storePageCacheId } from '../../../../actions/searchActions';
import MasterConfig from '../../../../config/Master';
import { getSearch, saveLastSearch } from '../../../../sdk';
import store from '../../../../store';
import { useEffect, useState } from 'react';

const fetchSearch = async ({ queryKey, pageParam = 1 }) => {
  const [, formData] = queryKey;
  const { profile, search } = store.getState();

  let pgid = search.pgid;

  if (pgid === null || typeof pgid === 'undefined') {
    const time = new Date().getTime();

    pgid = `${profile.hash_id}.${time}`;
    store.dispatch(storePageCacheId({ pgid }));
  }

  try {
    const response = await getSearch(formData, pageParam, true, pgid);

    await saveLastSearch(null, pageParam === 1);

    return response.data.data;
  } catch (error) {
    throw new Error(error);
  }
};

const useFetchSearch = (searchFilters: any): any => {
  const queryKey = ['search', searchFilters];
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return useInfiniteQuery(queryKey, fetchSearch, {
    enabled: isMounted,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage?.meta?.pagination?.total_pages === null) {
        return null;
      }

      if (lastPage?.meta?.pagination?.total <= lastPage?.meta?.pagination?.per_page) {
        return null;
      }

      return pages.length + 1;
    },
    onSuccess: results => {
      const { pages } = results;

      if (pages.length === 0) {
        return;
      }

      const lastResult = pages[pages.length - 1];

      const { meta } = lastResult;

      const page = meta.pagination;
      const timestamp = meta ? meta.request.datetime : 0;

      // store pagination details.
      const metaValues = {
        search_current_page: page.current_page,
        search_total_pages: page.total_pages,
        search_last_time: timestamp,
        search_max_limit: page?.max_page_limit ?? null,
        search_timestamp: meta && meta.search_timestamp,
      };

      const newMetaValues =
        MasterConfig.searchMicroserviceBoost && meta && meta.featured_profiles && meta.featured_profiles.data
          ? {
              ...metaValues,
              search_featured_profiles: meta.featured_profiles.data,
            }
          : metaValues;

      store.dispatch(storeMeta(newMetaValues));
    },
    onError: error => {
      console.error('Failed Search', error);
      store.dispatch(failedSearch());
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false, // Prevent unnecessary refetches when switching tabs
    keepPreviousData: true, // Keep showing previous data while fetching new data
    retry: 2, // Retry failed requests twice
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

export default useFetchSearch;
